import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const dest = location.state?.from || "/dashboard";

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1591561954557-26941169b49e?auto=format&fit=crop&q=80&w=1200" alt="" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-[#1A2024]/40"/>
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <h2 className="font-display text-4xl leading-tight">Welcome back to the marketplace built for us.</h2>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="font-display text-2xl mb-8 inline-block">AfriMarket</Link>
          <h1 className="font-display text-3xl mb-2">Log in</h1>
          <p className="text-muted-foreground text-sm mb-8">Welcome back. Sign in to keep selling, chatting and shopping.</p>

          <Button type="button" variant="outline" onClick={handleGoogle} className="w-full h-12 rounded-md border-black/20 mb-4" data-testid="google-login-btn">
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC04" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continue with Google
          </Button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-black/10"/><span className="text-xs text-muted-foreground">or with email</span><div className="flex-1 h-px bg-black/10"/>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="label-overline">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-2"/>
            </div>
            <div>
              <Label htmlFor="password" className="label-overline">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-2"/>
              <div className="mt-2 text-right">
                <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-[#C05A3C]" data-testid="login-forgot-link">
                  Forgot password?
                </Link>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-12 bg-[#C05A3C] hover:bg-[#D46B4E] rounded-md" data-testid="login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : "Log in"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6">
            New here? <Link to="/register" className="text-[#C05A3C] font-medium hover:underline" data-testid="link-register">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
